import { CstParser, Lexer, createToken } from 'chevrotain';

// ============================================================================
// RESERVED KEYWORDS - Cannot be used as variable names
// ============================================================================
const RESERVED_KEYWORDS = new Set([
  // Percentage keywords
  'of',
  'off',
  // Indian number words
  'lakh',
  'lakhs',
  'crore',
  'crores',
  // Western number words
  'thousand',
  'thousands',
  'million',
  'millions',
  'billion',
  'billions',
  'trillion',
  'trillions',
  // Currency words
  'rupee',
  'rupees',
  'dollar',
  'dollars',
  'euro',
  'euros',
  'pound',
  'pounds',
  'yen',
  'yens',
  'rs',
  // Currency codes
  'inr',
  'usd',
  'eur',
  'gbp',
  'jpy',
  'aud',
  'cad',
  'chf',
  'cny',
]);

// ============================================================================
// LEXER (Tokenizer) - Define all the tokens our language recognizes
// ============================================================================

// Currency symbols and keywords
const CurrencySymbol = createToken({
  name: 'CurrencySymbol',
  pattern: /[₹$€£¥]/,
});

const CurrencyCode = createToken({
  name: 'CurrencyCode',
  pattern: /\b(INR|USD|EUR|GBP|JPY|AUD|CAD|CHF|CNY)\b/i,
});

const CurrencyWord = createToken({
  name: 'CurrencyWord',
  pattern: /\b(rupees?|dollars?|euros?|pounds?|yens?|rs\.?)\b/i,
});

// Line references
const LineReference = createToken({
  name: 'LineReference',
  pattern: /@(?:line)?(\d+)/i,
});

// Number formats - Indian
const IndianNumberAbbrev = createToken({
  name: 'IndianNumberAbbrev',
  pattern: /\b(\d+(?:\.\d+)?)\s*([LC])\b/i, // 2L, 3C
});

const IndianNumberWord = createToken({
  name: 'IndianNumberWord',
  pattern: /\b(\d+(?:\.\d+)?)\s*(lakhs?|crore?s?)\b/i,
});

// Number formats - Western
const WesternNumberAbbrev = createToken({
  name: 'WesternNumberAbbrev',
  pattern: /\b(\d+(?:\.\d+)?)\s*([KMBT])\b/i, // 10K, 5M, 1B, 2T
});

const WesternNumberWord = createToken({
  name: 'WesternNumberWord',
  pattern: /\b(\d+(?:\.\d+)?)\s*(thousands?|millions?|billions?|trillions?)\b/i,
});

// Percentage keywords
const PercentOf = createToken({
  name: 'PercentOf',
  pattern: /\bof\b/i,
});

const PercentOff = createToken({
  name: 'PercentOff',
  pattern: /\boff\b/i,
});

// Operators
const Plus = createToken({ name: 'Plus', pattern: /\+/ });
const Minus = createToken({ name: 'Minus', pattern: /-/ });
const Multiply = createToken({ name: 'Multiply', pattern: /\*/ });
const Divide = createToken({ name: 'Divide', pattern: /\// });
const Power = createToken({ name: 'Power', pattern: /\^/ });
const Percent = createToken({ name: 'Percent', pattern: /%/ });
const Equals = createToken({ name: 'Equals', pattern: /=/ });

// Parentheses
const LParen = createToken({ name: 'LParen', pattern: /\(/ });
const RParen = createToken({ name: 'RParen', pattern: /\)/ });

// Identifiers and Numbers
const Identifier = createToken({
  name: 'Identifier',
  pattern: /[a-zA-Z_][a-zA-Z0-9_]*/,
});

const Number = createToken({
  name: 'Number',
  pattern: /\d+(?:\.\d+)?/,
});

// Whitespace (skipped)
const WhiteSpace = createToken({
  name: 'WhiteSpace',
  pattern: /\s+/,
  group: Lexer.SKIPPED,
});

// Comments (skipped)
const Comment = createToken({
  name: 'Comment',
  pattern: /#[^\n\r]*/,
  group: Lexer.SKIPPED,
});

const LineComment = createToken({
  name: 'LineComment',
  pattern: /\/\/[^\n\r]*/,
  group: Lexer.SKIPPED,
});

// Token list - ORDER MATTERS! More specific tokens should come first
const allTokens = [
  WhiteSpace,
  Comment,
  LineComment,
  // Keywords and special patterns (must come before Identifier)
  PercentOff, // Must come before PercentOf
  PercentOf,
  CurrencyCode,
  CurrencyWord,
  LineReference,
  IndianNumberAbbrev,
  IndianNumberWord,
  WesternNumberAbbrev,
  WesternNumberWord,
  // Operators
  Power, // Must come before other operators
  Equals,
  Plus,
  Minus,
  Multiply,
  Divide,
  Percent,
  // Delimiters
  LParen,
  RParen,
  CurrencySymbol,
  // Literals
  Identifier,
  Number,
];

const CalcLexer = new Lexer(allTokens);

// ============================================================================
// PARSER - Define the grammar rules
// ============================================================================

class TextCalciParser extends CstParser {
  constructor() {
    super(allTokens);
    this.performSelfAnalysis();
  }

  // Top level - either an assignment or an expression
  public statement = this.RULE('statement', () => {
    this.OR([
      { ALT: () => this.SUBRULE(this.assignment) },
      { ALT: () => this.SUBRULE(this.expression) },
    ]);
  });

  // Assignment: identifier = expression
  private assignment = this.RULE('assignment', () => {
    this.CONSUME(Identifier);
    this.CONSUME(Equals);
    this.SUBRULE(this.expression);
  });

  // Expression with addition and subtraction
  private expression = this.RULE('expression', () => {
    this.SUBRULE(this.term);
    this.MANY(() => {
      // First, consume the operator (+ or -)
      this.OR([
        { ALT: () => this.CONSUME(Plus) },
        { ALT: () => this.CONSUME(Minus) },
      ]);

      // Then consume the term
      this.SUBRULE2(this.term);

      // Optionally consume percent
      this.OPTION(() => {
        this.CONSUME(Percent);
      });
    });
  });

  // Term with multiplication and division
  private term = this.RULE('term', () => {
    this.SUBRULE(this.percentageExpression);
    this.MANY(() => {
      this.OR([
        {
          ALT: () => {
            this.CONSUME(Multiply);
            this.SUBRULE2(this.percentageExpression);
          },
        },
        {
          ALT: () => {
            this.CONSUME(Divide);
            this.SUBRULE3(this.percentageExpression);
          },
        },
      ]);
    });
  });

  // Percentage expressions: 20% of x, 10% off x
  // Note: bare % (like "10%") is NOT handled here, only "10% of x" or "10% off x"
  // Bare % is handled at the expression level (e.g., "x + 10%")
  private percentageExpression = this.RULE('percentageExpression', () => {
    this.SUBRULE(this.power);
    // Only consume % if followed by 'of' or 'off'
    // Use GATE to check if next token (after %) is 'of' or 'off' before entering
    this.OPTION({
      GATE: () => {
        // Look ahead: current token should be %, next should be 'of' or 'off'
        const la1 = this.LA(1);
        const la2 = this.LA(2);
        return (
          la1.tokenType === Percent &&
          (la2.tokenType === PercentOf || la2.tokenType === PercentOff)
        );
      },
      DEF: () => {
        this.OR([
          {
            ALT: () => {
              this.CONSUME(Percent);
              this.CONSUME(PercentOf);
              this.SUBRULE2(this.power);
            },
          },
          {
            ALT: () => {
              this.CONSUME2(Percent);
              this.CONSUME(PercentOff);
              this.SUBRULE3(this.power);
            },
          },
        ]);
      },
    });
  });

  // Power/exponentiation
  private power = this.RULE('power', () => {
    this.SUBRULE(this.atomic);
    this.MANY(() => {
      this.CONSUME(Power);
      this.SUBRULE2(this.atomic);
    });
  });

  // Atomic values: numbers, identifiers, parentheses, special formats
  private atomic = this.RULE('atomic', () => {
    this.OR([
      // Currency with number: ₹100, $50, USD 100
      {
        ALT: () => {
          this.OR1([
            { ALT: () => this.CONSUME(CurrencySymbol) },
            { ALT: () => this.CONSUME(CurrencyCode) },
          ]);
          this.SUBRULE(this.numericValue);
        },
      },
      // Number with currency word: 100 rupees
      {
        ALT: () => {
          this.SUBRULE2(this.numericValue);
          this.OPTION(() => {
            this.CONSUME(CurrencyWord);
          });
        },
      },
      // Parenthesized expression
      {
        ALT: () => {
          this.CONSUME(LParen);
          this.SUBRULE(this.expression);
          this.CONSUME(RParen);
        },
      },
    ]);
  });

  // Numeric value: plain number, formatted number, identifier, or line reference
  private numericValue = this.RULE('numericValue', () => {
    this.OR([
      { ALT: () => this.CONSUME(IndianNumberAbbrev) },
      { ALT: () => this.CONSUME(IndianNumberWord) },
      { ALT: () => this.CONSUME(WesternNumberAbbrev) },
      { ALT: () => this.CONSUME(WesternNumberWord) },
      { ALT: () => this.CONSUME(LineReference) },
      { ALT: () => this.CONSUME(Identifier) },
      { ALT: () => this.CONSUME(Number) },
    ]);
  });
}

// Create parser instance
const parserInstance = new TextCalciParser();

// ============================================================================
// INTERPRETER - Evaluate the parsed AST
// ============================================================================

export interface EvaluationContext {
  variables: Record<string, number>;
  lines: Array<{ input: string; result: string | null }>;
  lineIndex: number;
}

export interface EvaluationResult {
  value: number;
  currencySymbol?: string;
  variableName?: string;
}

// Base visitor class for traversing the CST
const BaseCstVisitor = parserInstance.getBaseCstVisitorConstructor();

class TextCalciInterpreter extends BaseCstVisitor {
  constructor() {
    super();
    this.validateVisitor();
  }

  statement(ctx: any, context: EvaluationContext): EvaluationResult {
    if (ctx.assignment) {
      return this.visit(ctx.assignment, context);
    }
    if (ctx.expression) {
      return this.visit(ctx.expression, context);
    }
    throw new Error('Invalid statement');
  }

  assignment(ctx: any, context: EvaluationContext): EvaluationResult {
    const varName = ctx.Identifier[0].image;

    // Check if variable name is a reserved keyword
    if (RESERVED_KEYWORDS.has(varName.toLowerCase())) {
      throw new Error(
        `Reserved keyword: "${varName}" cannot be used as variable name`
      );
    }

    const value = this.visit(ctx.expression, context);

    // Store variable in context
    context.variables[varName] = value.value;

    return {
      value: value.value,
      currencySymbol: value.currencySymbol,
      variableName: varName,
    };
  }

  expression(ctx: any, context: EvaluationContext): EvaluationResult {
    let result = this.visit(ctx.term[0], context);

    if (!ctx.Plus && !ctx.Minus) {
      return result;
    }

    // Build list of operations in source order
    const operations: Array<{
      type: '+' | '-';
      position: number;
    }> = [];

    if (ctx.Plus) {
      ctx.Plus.forEach((token: any) => {
        operations.push({ type: '+', position: token.startOffset });
      });
    }

    if (ctx.Minus) {
      ctx.Minus.forEach((token: any) => {
        operations.push({ type: '-', position: token.startOffset });
      });
    }

    // Sort by position to maintain correct order
    operations.sort((a, b) => a.position - b.position);

    // Check which operations have trailing percentages
    const percentCount = ctx.Percent?.length || 0;
    const percentPositions = new Set<number>();
    if (ctx.Percent) {
      ctx.Percent.forEach((token: any) => {
        percentPositions.add(token.startOffset);
      });
    }

    // Evaluate each operation in order
    operations.forEach((op, idx) => {
      const termValue = this.visit(ctx.term[idx + 1], context).value;

      // Check if this operation has a trailing % by looking at the next token position
      // The percent immediately follows the term, so we check if there's a percent token
      // between this operation and the next
      const hasPercent = idx < percentCount;

      if (hasPercent) {
        // x + 10% means x + (x * 10/100)
        if (op.type === '+') {
          result = { value: result.value + (result.value * termValue) / 100 };
        } else {
          result = { value: result.value - (result.value * termValue) / 100 };
        }
      } else {
        if (op.type === '+') {
          result = { value: result.value + termValue };
        } else {
          result = { value: result.value - termValue };
        }
      }
    });

    return result;
  }

  term(ctx: any, context: EvaluationContext): EvaluationResult {
    let result = this.visit(ctx.percentageExpression[0], context);

    if (ctx.Multiply || ctx.Divide) {
      for (let i = 1; i < ctx.percentageExpression.length; i++) {
        const right = this.visit(ctx.percentageExpression[i], context);
        if (ctx.Multiply && ctx.Multiply[i - 1]) {
          result = { value: result.value * right.value };
        } else if (ctx.Divide && ctx.Divide[i - 1]) {
          // Check for division by zero
          if (right.value === 0) {
            throw new Error('Division by zero');
          }
          result = { value: result.value / right.value };
        }
      }
    }

    return result;
  }

  percentageExpression(ctx: any, context: EvaluationContext): EvaluationResult {
    const base = this.visit(ctx.power[0], context);

    if (ctx.Percent) {
      if (ctx.PercentOf) {
        // 20% of x = (20/100) * x
        const target = this.visit(ctx.power[1], context);
        return { value: (base.value / 100) * target.value };
      } else if (ctx.PercentOff) {
        // 20% off x = x - (x * 20/100)
        const target = this.visit(ctx.power[1], context);
        return { value: target.value - (target.value * base.value) / 100 };
      }
    }

    return base;
  }

  power(ctx: any, context: EvaluationContext): EvaluationResult {
    let result = this.visit(ctx.atomic[0], context);

    if (ctx.Power) {
      for (let i = 1; i < ctx.atomic.length; i++) {
        const exponent = this.visit(ctx.atomic[i], context);
        result = { value: Math.pow(result.value, exponent.value) };
      }
    }

    return result;
  }

  atomic(ctx: any, context: EvaluationContext): EvaluationResult {
    // Currency prefix
    let currencySymbol: string | undefined;
    if (ctx.CurrencySymbol) {
      currencySymbol = ctx.CurrencySymbol[0].image;
    } else if (ctx.CurrencyCode) {
      const code = ctx.CurrencyCode[0].image.toUpperCase();
      const symbolMap: Record<string, string> = {
        INR: '₹',
        USD: '$',
        EUR: '€',
        GBP: '£',
        JPY: '¥',
        AUD: '$',
        CAD: '$',
        CHF: 'CHF',
        CNY: '¥',
      };
      currencySymbol = symbolMap[code] || code;
    }

    // Parenthesized expression
    if (ctx.expression) {
      const result = this.visit(ctx.expression, context);
      return {
        ...result,
        currencySymbol: currencySymbol || result.currencySymbol,
      };
    }

    // Numeric value
    const numValue = this.visit(ctx.numericValue[0], context);

    // Currency suffix
    if (ctx.CurrencyWord && !currencySymbol) {
      const word = ctx.CurrencyWord[0].image.toLowerCase();
      if (word.startsWith('rupee') || word.startsWith('rs')) {
        currencySymbol = '₹';
      } else if (word.startsWith('dollar')) {
        currencySymbol = '$';
      } else if (word.startsWith('euro')) {
        currencySymbol = '€';
      } else if (word.startsWith('pound')) {
        currencySymbol = '£';
      } else if (word.startsWith('yen')) {
        currencySymbol = '¥';
      }
    }

    return {
      ...numValue,
      currencySymbol: currencySymbol || numValue.currencySymbol,
    };
  }

  numericValue(ctx: any, context: EvaluationContext): EvaluationResult {
    // Indian number abbreviations: 2L, 3C
    if (ctx.IndianNumberAbbrev) {
      const match = ctx.IndianNumberAbbrev[0].image.match(
        /(\d+(?:\.\d+)?)\s*([LC])/i
      );
      if (match) {
        const num = parseFloat(match[1]);
        const unit = match[2].toUpperCase();
        const multiplier = unit === 'L' ? 100000 : 10000000; // L=lakh, C=crore
        return { value: num * multiplier };
      }
    }

    // Indian number words: 2 lakh, 3 crore
    if (ctx.IndianNumberWord) {
      const match = ctx.IndianNumberWord[0].image.match(
        /(\d+(?:\.\d+)?)\s*(lakhs?|crore?s?)/i
      );
      if (match) {
        const num = parseFloat(match[1]);
        const unit = match[2].toLowerCase();
        const multiplier = unit.startsWith('lakh') ? 100000 : 10000000;
        return { value: num * multiplier };
      }
    }

    // Western number abbreviations: 10K, 5M, 1B, 2T
    if (ctx.WesternNumberAbbrev) {
      const match = ctx.WesternNumberAbbrev[0].image.match(
        /(\d+(?:\.\d+)?)\s*([KMBT])/i
      );
      if (match) {
        const num = parseFloat(match[1]);
        const unit = match[2].toUpperCase();
        const multipliers: Record<string, number> = {
          K: 1000,
          M: 1000000,
          B: 1000000000,
          T: 1000000000000,
        };
        return { value: num * multipliers[unit] };
      }
    }

    // Western number words: 10 thousand, 5 million
    if (ctx.WesternNumberWord) {
      const match = ctx.WesternNumberWord[0].image.match(
        /(\d+(?:\.\d+)?)\s*(thousands?|millions?|billions?|trillions?)/i
      );
      if (match) {
        const num = parseFloat(match[1]);
        const unit = match[2].toLowerCase();
        const multipliers: Record<string, number> = {
          thousand: 1000,
          thousands: 1000,
          million: 1000000,
          millions: 1000000,
          billion: 1000000000,
          billions: 1000000000,
          trillion: 1000000000000,
          trillions: 1000000000000,
        };
        return { value: num * multipliers[unit] };
      }
    }

    // Line reference: @line3, @3
    if (ctx.LineReference) {
      const match = ctx.LineReference[0].image.match(/@(?:line)?(\d+)/i);
      if (match) {
        const lineNum = parseInt(match[1]);
        const lineIndex = lineNum - 1;

        // Check for invalid line numbers (negative)
        if (lineIndex < 0) {
          throw new Error(`Line ${lineNum} does not exist`);
        }

        // Check if referencing a future line (forward reference)
        // This includes lines that don't exist yet AND lines that exist but haven't been evaluated
        if (lineIndex >= context.lineIndex) {
          throw new Error(`Line ${lineNum} result used before its evaluation`);
        }

        // Now check if the line exists in the evaluated lines array
        if (lineIndex >= context.lines.length) {
          throw new Error(`Line ${lineNum} does not exist`);
        }

        const lineResult = context.lines[lineIndex].result;

        // Check if line has a result
        if (!lineResult) {
          throw new Error(`Line ${lineNum} has no value`);
        }

        return { value: parseFloat(lineResult) };
      }
      throw new Error('Invalid line reference');
    }

    // Variable reference
    if (ctx.Identifier) {
      const varName = ctx.Identifier[0].image;
      if (varName in context.variables) {
        return { value: context.variables[varName] };
      }
      throw new Error(`Undefined variable: ${varName}`);
    }

    // Plain number
    if (ctx.Number) {
      return { value: parseFloat(ctx.Number[0].image) };
    }

    throw new Error('Invalid numeric value');
  }
}

// Create interpreter instance
const interpreter = new TextCalciInterpreter();

// ============================================================================
// PUBLIC API
// ============================================================================

export function parseAndEvaluate(
  input: string,
  context: EvaluationContext
): EvaluationResult | { error: string } {
  try {
    // Skip empty lines and comments
    const trimmed = input.trim();
    if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('//')) {
      return { error: 'empty or comment' };
    }

    // Tokenize
    const lexResult = CalcLexer.tokenize(input);

    if (lexResult.errors.length > 0) {
      return { error: lexResult.errors[0].message };
    }

    // Parse
    parserInstance.input = lexResult.tokens;
    const cst = parserInstance.statement();

    if (parserInstance.errors.length > 0) {
      // Check if this is a reserved keyword being used as variable name
      const firstToken = lexResult.tokens[0];
      if (firstToken && lexResult.tokens[1]?.tokenType.name === 'Equals') {
        const keyword = firstToken.image.toLowerCase();
        if (RESERVED_KEYWORDS.has(keyword)) {
          return { error: `"${firstToken.image}" is a reserved keyword` };
        }
      }

      // Provide user-friendly error messages
      const errorMsg = parserInstance.errors[0].message;

      // Simplify common parser errors
      if (errorMsg.includes('Expecting')) {
        return { error: 'Invalid syntax' };
      }

      return { error: 'Syntax error' };
    }

    // Evaluate
    const result = interpreter.visit(cst, context);
    return result;
  } catch (error) {
    // Provide cleaner error messages for common issues
    if (error instanceof Error) {
      const msg = error.message;

      // Math errors
      if (msg.includes('Division by zero')) {
        return { error: 'Division by zero' };
      }

      // Line reference errors
      if (msg.includes('used before its evaluation')) {
        return { error: msg };
      }
      if (msg.includes('does not exist')) {
        return { error: msg };
      }
      if (msg.includes('has no value')) {
        return { error: msg };
      }

      // Reserved keyword error
      if (msg.includes('Reserved keyword')) {
        return { error: msg };
      }

      // Undefined variable error
      if (msg.includes('Undefined variable')) {
        return { error: msg };
      }

      // Generic error
      return { error: msg };
    }

    return { error: String(error) };
  }
}

// Get list of token types for syntax highlighting
export function getTokensForHighlighting(input: string) {
  const lexResult = CalcLexer.tokenize(input);
  return lexResult.tokens.map(token => ({
    type: token.tokenType.name,
    image: token.image,
    startOffset: token.startOffset,
    endOffset: token.endOffset,
  }));
}
